import React from 'react';
import styled from '@emotion/styled';
import Image from 'next/image';

const ScmIconTab = ({
  tab,
  index,
  selectedIndex,
  onSelect,
  selectedIconBarColor,
}) => {
  return (
    <TabWrapper onClick={() => onSelect(index)}>
      <SelectedBar
        data-testid="selected-bar"
        selected={selectedIndex === index}
        selectedIconBarColor={selectedIconBarColor}
      />
      <ResponsiveImage
        src={tab?.fields?.iconImage?.url}
        alt={
          tab?.fields?.iconImage?.label ||
          tab?.fields?.iconTitle ||
          tab?.fields?.title
        }
        width={70}
        height={70}
      />
      <IconTitleContainer>
        {tab?.fields?.iconTitle || tab?.fields?.title || ''}
      </IconTitleContainer>
    </TabWrapper>
  );
};

const TabWrapper = styled('div')(({ theme }) => ({
  cursor: 'pointer',
  textAlign: 'center',
  paddingTop: '20px',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  gap: '10px',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  width: '208px',
  height: '164px',
  [theme.breakpoints.down('lg')]: {
    width: '180px',
    height: '130px',
  },
  [theme.breakpoints.down('md')]: {
    width: '120px',
    height: '100px',
    paddingTop: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '50px',
    height: '170px',
    gap: '3px',
  },
}));

const SelectedBar = styled('div')(({ selected, selectedIconBarColor }) => ({
  position: 'absolute',
  top: 0,
  width: '160px',
  height: selected ? '12px' : '0px',
  background: selectedIconBarColor,
  transition: 'height 0.2s cubic-bezier(0.4,0,0.2,1)',
}));

const IconTitleContainer = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: '20px',
  padding: '2px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  position: 'relative',
  background: 'white',
  fontFamily: 'Source Sans Pro',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '24px',
  letterSpacing: 0,
  textAlign: 'center',
  color:
    theme && theme.palette && theme.palette.black && theme.palette.black.main
      ? theme.palette.black.main
      : '#000000',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  //Ensure visibility in iPhone 14 Pro landscape (430px)
  '@media (max-width: 430px) and (orientation: landscape)': {
    fontWeight: 700,
    fontSize: '18px', // larger font for visibility
    lineHeight: '22px',
    minHeight: '44px', // more height for 2 lines
    maxWidth: '100%',
    padding: '8px 0',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    textAlign: 'center',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  // Also improve for 600px and 900px landscape
  '@media (max-width: 600px) and (orientation: landscape)': {
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '18px',
    minHeight: '28px',
    padding: '4px 0',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    textAlign: 'center',
    width: '100%',
  },
  '@media (max-width: 900px) and (orientation: landscape)': {
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '20px',
    minHeight: '32px',
    padding: '4px 0',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    textAlign: 'center',
    width: '100%',
  },
  [theme.breakpoints.down('lg')]: {
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    textAlign: 'center',
    width: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    minHeight: '14px',
  },
  [theme.breakpoints.down('sm')]: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '100%',
    letterSpacing: '0%',
    textAlign: 'center',
    width: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    minHeight: '12px',
  },
}));

const ResponsiveImage = styled(Image)(({ theme }) => ({
  objectFit: 'fill',
  zIndex: 5,
  width: '70px',
  height: '70px',
  minWidth: '70px',
  minHeight: '70px',
  maxWidth: '70px',
  maxHeight: '70px',
  '@media (max-width: 900px) and (orientation: landscape)': {
    width: '60px',
    height: '60px',
    minWidth: '60px',
    minHeight: '60px',
    maxWidth: '60px',
    maxHeight: '60px',
    marginTop: '0px',
  },
  '@media (max-width: 600px) and (orientation: landscape)': {
    width: '45px',
    height: '45px',
    minWidth: '45px',
    minHeight: '45px',
    maxWidth: '45px',
    maxHeight: '45px',
    marginTop: '0px',
  },
  '@media (max-width: 430px) and (orientation: landscape)': {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    maxWidth: '40px',
    maxHeight: '40px',
    marginTop: '0px',
  },
  [theme.breakpoints.down('md')]: {
    width: '45px',
    height: '45px',
    minWidth: '45px',
    minHeight: '45px',
    maxWidth: '45px',
    maxHeight: '45px',
    marginTop: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '-20px',
  },
}));

export default ScmIconTab;
